import React from "react";
import styled from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import CaseService from "../../../services/CaseService";
import { getErrorMessage } from "../../../helpers/functions";
import moment from "moment";
import { Formik } from "formik";
import FormField from "../../common/FormField/FormField";
import { AddHearingDateValidation } from "../../../helpers/validationSchema";
import { CheckBoxLabel, CustomCheckbox } from "../../common/FormInputs";

const CHANNEL_OPTIONS = [
  // { key: "sms", label: "SMS" },
  // { key: "email", label: "Email" },
  // { key: "whatsapp", label: "WhatsApp" },
];

const HEARING_MODE_OPTIONS = [
  { label: "Physical", value: "Physical" },
  { label: "Online", value: "Online" },
  { label: "Hybrid", value: "Hybrid" },
];

export default function HearingDateModal({
  openHearing,
  setOpenHearing,
  caseDetails,
  setRefreshDetails,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  let id = caseDetails?.id;

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: `Uploading NextHearing Date` });
      const date = moment(new Date(values.startDate)).format("YYYY-MM-DD");
      const time = moment(new Date(values?.startTime)).format("HH:mm:ss");
      const selectedTime = moment(`${date} ${time}`);
      const postData = {
        channel: {
          sms: values.channel.sms,
          email: values.channel.email,
          whatsapp: values.channel.whatsapp,
        },
        nextHearingDate: {
          date: values.startDate
            ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
            : null,
          time: values?.startTime
            ? moment(selectedTime).format("DD/MM/YYYY HH:mm:ss")
            : null,
          hearingType: values?.hearingType,
          hearingMode: values?.hearingMode,
          remarks: values?.remarks,
        },
      };
      const res = await CaseService.nextHearingDate(postData, id);
      if (res) {
        enqueueSnackbar("Hearing Date Updated Successfully", {
          variant: "success",
        });
        setRefreshDetails(true);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setOpenHearing(false);
    }
  }

  const inputs = [
    {
      name: "hearingType",
      label: "Hearing Type",
      type: "text",
      required: true,
    },
    {
      name: "hearingMode",
      label: "Hearing Mode",
      type: "select",
      menuItems: HEARING_MODE_OPTIONS,
      required: true,
    },
    {
      name: "remarks",
      label: "Remarks",
      type: "text",
      required: true,
    },
  ];

  return (
    <Modal
      open={openHearing}
      onClose={() => setOpenHearing(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <Container>
          <HeadingContainer>
            <Heading>Hearing Date</Heading>
            <CloseModal
              onClick={() => setOpenHearing(false)}
              src={require("../../../assets/images/closeModal.svg")}
            />
          </HeadingContainer>
          <FormContainer>
            <Formik
              initialValues={{
                startDate: openHearing?.id ? null : null,
                startTime: openHearing?.id
                  ? new Date(caseDetails.nextHearingDate?.date)
                  : null,
                hearingType: openHearing?.hearingType || "",
                hearingMode: openHearing?.hearingMode || "",
                remarks: openHearing?.remarks || "",
                channel: {
                  sms: true,
                  email: false,
                  whatsapp: false,
                },
              }}
              validationSchema={AddHearingDateValidation}
              onSubmit={onFormSubmit}
              validateOnBlur
              validateOnChange
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                handleBlur,
                setFieldValue,
              }) => (
                <StyledForm onSubmit={handleSubmit} className="mis-dashboard">
                  <div className="date-time">
                    <div className="date">
                      <FormField
                        input={{
                          name: "startDate",
                          label: "Date",
                          type: "date",
                        }}
                        placeholderText="N/A"
                        minDate={new Date()}
                        {...{
                          touched,
                          errors,
                          values,
                          handleChange,
                          handleBlur,
                          setFieldValue,
                        }}
                      />
                    </div>
                    <div className="time">
                      {values.startDate ? (
                        <FormField
                          input={{
                            name: "startTime",
                            label: "Time (IST)",
                            type: "time",
                            required: true,
                          }}
                          placeholderText="N/A"
                          {...{
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }}
                        />
                      ) : (
                        <FormField
                          input={{
                            name: "startTime",
                            label: "Time (IST)",
                            type: "time",
                          }}
                          placeholderText="N/A"
                          {...{
                            touched,
                            errors,
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="reason">
                    {inputs?.map((input, index) => (
                      <FormField
                        key={index}
                        {...{
                          input,
                          values,
                          errors,
                          touched,
                          handleBlur,
                          handleChange,
                          menuItems: input.menuItems,
                          setFieldValue,
                        }}
                      />
                    ))}
                  </div>

                  <ChannelSection>
                    {/* <ChannelLabel>Notify Via</ChannelLabel> */}
                    <ChannelRow>
                      {CHANNEL_OPTIONS.map(({ key, label }) => (
                        <CheckboxItem key={key}>
                          <HiddenCheckbox
                            id={`channel-${key}`}
                            type="checkbox"
                            checked={values.channel[key]}
                            onChange={(e) =>
                              setFieldValue(`channel.${key}`, e.target.checked)
                            }
                          />
                          <CustomCheckbox
                            checked={values.channel[key]}
                            onClick={() =>
                              setFieldValue(
                                `channel.${key}`,
                                !values.channel[key],
                              )
                            }
                          />
                          <CheckBoxLabel htmlFor={`channel-${key}`}>
                            {label}
                          </CheckBoxLabel>
                        </CheckboxItem>
                      ))}
                    </ChannelRow>
                  </ChannelSection>

                  <ButtonContainer>
                    <PrimaryOutlinedCloseCTAButton
                      style={{ width: "45%", height: "38px" }}
                      onClick={() => setOpenHearing(false)}
                    >
                      Cancel
                    </PrimaryOutlinedCloseCTAButton>
                    <PrimaryCTAButton
                      style={{ width: "45%", height: "38px" }}
                      type="submit"
                      onClick={handleSubmit}
                    >
                      Submit
                    </PrimaryCTAButton>
                  </ButtonContainer>
                </StyledForm>
              )}
            </Formik>
          </FormContainer>
        </Container>
      </DrawerContainer>
    </Modal>
  );
}

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 450px;
  }
`;

const HeadingContainer = styled.div`
  padding-top: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 25px;
  justify-content: space-around;
`;

const CloseModal = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
  cursor: pointer;
  position: absolute;
  right: 30px;
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

const StyledForm = styled.form`
  width: 100%;
`;

const FormContainer = styled.div`
  padding: 15px;
  .link-container {
    margin: 12px;
  }
  .date-time {
    display: flex;
    .date {
      margin: 12px;
      width: 100%;
    }
    .time {
      margin: 12px;
      width: 100%;
    }
  }
  .reason {
    margin: 12px;
  }
`;

/* ── Channel Checkbox Styles ── */

const ChannelSection = styled.div`
  margin: 4px 12px 12px;
`;

const ChannelLabel = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #9ca3af;
  margin-bottom: 10px;
`;

const ChannelRow = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
`;

const CheckboxItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  display: none;
`;
